import {
	alias,
	custom,
	date,
	deserialize,
	getDefaultModelSchema,
	identifier,
	list,
	object,
	serializable,
} from "serializr";
import { IJsonObject } from "../api";
import { Attachment } from "../attachments";
import { Opportunity } from "../opportunities/Opportunity";
import { User } from "../users";
import { ProjectBudget } from "./ProjectBudget";
import { ProjectMember } from "./ProjectMember";
import { ProjectStats } from "./ProjectStats";

interface IProps {
	id?: number | null;
	accountId?: number;
	name?: string;
	description?: string;
	color?: string;
	budget?: number;
	timeLogged?: number;
	estimatedHours?: number;
	status?: string;
	startDate?: Date | null;
	endDate?: Date | null;
	completedDate?: Date | null;
	group?: string;
	members?: ProjectMember[];
	attachments?: Attachment[];
	taskAttachments?: Attachment[];
	opportunities?: Opportunity[];
	stats?: ProjectStats | null;
	userStats?: ProjectStats | null;
	watchers?: User[];
	budgets?: ProjectBudget[];
	pivot?: any;
}

export const projectColorMap: IJsonObject = {
	"#e8384f": "red",
	"#fd612c": "orange",
	"#fd9a00": "light_orange",
	"#eec300": "yellow",
	"#a4cf30": "light_green",
	"#62d26f": "green",
	"#37c5ab": "blue_green",
	"#20aaea": "aqua",
	"#4186e0": "blue",
	"#6558ee": "indigo",
	"#aa62e3": "purple",
	"#e362e3": "magenta",
	"#ea4e9d": "hot_pink",
	"#fc91ad": "pink",
	"#8da3a6": "gray",
};

export class Project {
	get timelogDownloadLink() {
		return `/api/v1/project/${this.id}/export-timelog`;
	}

	get tasksDownloadLink() {
		return `/api/v1/project/${this.id}/export-tasks`;
	}

	get dashboardLink() {
		return `/projects/${this.id}`;
	}
	public static fromJson(json: IJsonObject): Project {
		return deserialize(Project, json);
	}

	public static fromJsonArray(json: IJsonObject[]): Project[] {
		return json.map((project: IJsonObject) => this.fromJson(project));
	}

	@serializable(identifier())
	public id: number | null;

	@serializable(alias("account_id"))
	public accountId: number;

	@serializable
	public name: string;

	@serializable
	public description: string;

	@serializable
	public status: string;

	@serializable
	public color: string;

	@serializable
	public group: string;

	@serializable
	public budget: number;

	@serializable(alias("time_logged"))
	public timeLogged: number;

	@serializable(alias("estimated_hours"))
	public estimatedHours: number;

	@serializable(alias("start_date", date()))
	public startDate: Date | null;

	@serializable(alias("end_date", date()))
	public endDate: Date | null;

	@serializable(alias("completed_date", date()))
	public completedDate: Date | null;

	@serializable(list(object(ProjectMember)))
	public members: ProjectMember[];

	@serializable(list(object(Attachment)))
	public attachments: Attachment[];

	@serializable(alias("task_attachments", list(object(Attachment))))
	public taskAttachments: Attachment[];

	@serializable(list(object(Opportunity)))
	public opportunities: Opportunity[];

	@serializable(object(ProjectStats))
	public stats: ProjectStats | null;

	@serializable(object(ProjectStats))
	public userStats: ProjectStats | null;

	@serializable(list(object(User)))
	public watchers: User[];

	public budgets: ProjectBudget[];

	@serializable(custom(modelValue => modelValue, jsonValue => jsonValue))
	public pivot: any;

	constructor({
		id = null,
		name = "",
		description = "",
		color = "blue",
		budget = 0,
		timeLogged = 0,
		estimatedHours = 0,
		status = "Not Started",
		startDate = null,
		endDate = null,
		completedDate = null,
		group = "",
		members = [],
		attachments = [],
		taskAttachments = [],
		accountId = 0,
		opportunities = [],
		stats = null,
		userStats = null,
		watchers = [],
		budgets = [],
		pivot = null,
	}: IProps = {}) {
		this.id = id;
		this.name = name;
		this.description = description;
		this.budget = budget;
		this.color = color;
		this.timeLogged = timeLogged;
		this.estimatedHours = estimatedHours;
		this.status = status;
		this.startDate = startDate;
		this.endDate = endDate;
		this.completedDate = completedDate;
		this.group = group;
		this.members = members;
		this.attachments = attachments;
		this.taskAttachments = taskAttachments;
		this.accountId = accountId;
		this.opportunities = opportunities;
		this.stats = stats;
		this.userStats = userStats;
		this.watchers = watchers;
		this.pivot = pivot;
		this.budgets = budgets;

		getDefaultModelSchema(Project).props.budgets = list(
			object(ProjectBudget),
		);
	}

	public isWatchedBy(user: User): boolean {
		const watcher = this.watchers.filter(u => {
			return u.id === user.id;
		});

		return watcher.length > 0;
	}
}
